import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Loader2, ShoppingBag } from "lucide-react";
import { api, formatApiError } from "../../api";
import { MEMBER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/sonner";
import { NotAvailablePanel } from "../../components/desk/NotAvailablePanel";

// UX-MEM-01 §1 SCR-P03-06: module-gated on `retail` (real `feature_entitlement` check, both
// server-side in routers/member_app.py AND mirrored here so the tab doesn't render a dead form).
// Wallet-first checkout (falls back to digital) — real balance decrement via retail_engine.
export default function Shop() {
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const productsQ = useQuery({ queryKey: ["member-products"], queryFn: async () => (await api.get("/member/products")).data });

  const buyMutation = useMutation({
    mutationFn: (productId) => api.post("/member/shop/purchase", { product_ref: productId, qty: 1, payment_mode: "wallet" }),
    onSuccess: () => {
      toast.success("Purchased — added to your account.");
      queryClient.invalidateQueries({ queryKey: ["member-membership"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (productsQ.isLoading) return <p className="text-sm flex items-center gap-2" style={{ color: "#A1A1AA" }}><Loader2 size={14} className="animate-spin" /> Loading…</p>;
  if (productsQ.data && !productsQ.data.enabled) {
    return (
      <div data-testid={MEMBER.shopPage}>
        <NotAvailablePanel testId={MEMBER.shopDisabledMessage} title="Shop isn't enabled for your gym" reason="Your gym hasn't turned on retail/shop for members yet." />
      </div>
    );
  }

  return (
    <div data-testid={MEMBER.shopPage} className="space-y-4">
      <motion.h1 {...fadeUp(reducedMotion)} className="font-heading text-2xl font-bold flex items-center gap-2"><ShoppingBag size={20} color="#D6202B" /> Shop</motion.h1>
      <div className="grid grid-cols-2 gap-3">
        {(productsQ.data?.products || []).slice(0, 24).map((p, i) => (
          <motion.div key={p.id} {...fadeUp(reducedMotion, 0.02 * i)} data-testid={MEMBER.productRow(p.id)} className="rounded-2xl p-4 flex flex-col gap-2" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
            <p className="text-sm font-semibold leading-tight line-clamp-2">{p.name}</p>
            <p className="text-xs" style={{ color: "#A1A1AA" }}>₹{(p.price / 100).toLocaleString("en-IN")}</p>
            <Button data-testid={MEMBER.productBuyButton(p.id)} size="sm" disabled={buyMutation.isPending} onClick={() => buyMutation.mutate(p.id)} className="w-full text-white active:scale-95 transition-transform mt-auto" style={{ background: "#D6202B" }}>Buy</Button>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
